import React, { useState } from "react";
import { Mail, Phone, MapPin, ArrowRight, CheckCircle2 } from "lucide-react";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { trackFormSubmit, trackPhoneClick } from "../lib/tracking";

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      trackFormSubmit("contact", { has_phone: !!form.phone });
      setDone(true);
      toast.success("Message received. We'll reply within one business day.");
    } catch {
      toast.error("Couldn't send your message.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-contact">
      <section className="alpha-gradient-hero border-b border-[color:var(--alpha-line)]">
        <div className="alpha-container pt-14 pb-10">
          <div className="alpha-overline">Contact Alpha</div>
          <h1 className="mt-3 text-4xl sm:text-[52px] font-display font-medium tracking-tight leading-[1.05]">Let's talk.</h1>
          <p className="mt-4 max-w-2xl text-[color:var(--alpha-mute)]">Our intake team operates across Victoria and Western Australia. We respond within one business day.</p>
        </div>
      </section>

      <section className="alpha-container py-14 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          {done ? (
            <div className="alpha-card p-10 text-center" data-testid="contact-success">
              <CheckCircle2 className="w-12 h-12 text-[color:var(--alpha-purple-700)] mx-auto" />
              <h2 className="mt-4 font-display text-2xl font-semibold">Thank you — we've received your message.</h2>
              <p className="mt-2 text-[color:var(--alpha-mute)]">A member of our team will be in touch shortly.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="alpha-card p-8 space-y-4" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <Input placeholder="Full name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="contact-name" />
                <Input type="email" placeholder="Email *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="contact-email" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="contact-phone" />
                <Input placeholder="Subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="contact-subject" />
              </div>
              <Textarea placeholder="Your message *" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={6} required data-testid="contact-message" />
              <button disabled={submitting} className="alpha-btn-primary" data-testid="contact-submit">
                {submitting ? "Sending…" : <>Send message <ArrowRight className="w-4 h-4" /></>}
              </button>
            </form>
          )}
        </div>

        <aside className="lg:col-span-5 space-y-4">
          <div className="alpha-card p-6">
            <div className="alpha-overline">General enquiries</div>
            <a href="mailto:hello@alphacommunitycare.com.au" className="mt-3 flex items-center gap-3 text-sm font-semibold"><Mail className="w-4 h-4 text-[color:var(--alpha-purple-700)]" /> hello@alphacommunitycare.com.au</a>
            <a href="tel:1300380692" onClick={() => trackPhoneClick("1300 380 692", "contact-sidebar")} className="mt-2 flex items-center gap-3 text-sm font-semibold"><Phone className="w-4 h-4 text-[color:var(--alpha-purple-700)]" /> 1300 380 692</a>
          </div>
          <div className="alpha-card p-6">
            <div className="alpha-overline">SIL &amp; housing</div>
            <a href="mailto:SIL@alphacommunitycare.com.au" className="mt-3 flex items-center gap-3 text-sm font-semibold"><Mail className="w-4 h-4 text-[color:var(--alpha-green-600)]" /> SIL@alphacommunitycare.com.au</a>
          </div>
          <div className="alpha-card p-6">
            <div className="alpha-overline">Locations</div>
            <div className="mt-3 flex items-start gap-3 text-sm">
              <MapPin className="w-4 h-4 text-[color:var(--alpha-purple-700)] mt-0.5" />
              <div>
                <div className="font-semibold">Victoria</div>
                <div className="text-[color:var(--alpha-mute)]">Docklands, Melbourne VIC</div>
              </div>
            </div>
            <div className="mt-3 flex items-start gap-3 text-sm">
              <MapPin className="w-4 h-4 text-[color:var(--alpha-purple-700)] mt-0.5" />
              <div>
                <div className="font-semibold">Western Australia</div>
                <div className="text-[color:var(--alpha-mute)]">West Perth, WA</div>
              </div>
            </div>
          </div>
          <div className="alpha-card p-6 alpha-gradient text-[color:var(--alpha-purple-50)]">
            <div className="font-display text-xl font-semibold">Urgent support?</div>
            <p className="text-white/85 text-sm mt-2">Our operations team is available for existing participants and urgent coordinator escalations.</p>
            <a href="tel:1300380692" className="mt-4 inline-flex items-center gap-2 bg-[color:var(--alpha-purple-50)] text-[color:var(--alpha-purple-800)] rounded-full px-4 py-2 text-sm font-semibold">
              <Phone className="w-4 h-4" /> 1300 380 692
            </a>
          </div>
        </aside>
      </section>
    </div>
  );
}
